import {
    Box,
    Text,
    Flex,
    IconButton,
    Tooltip,
    VStack,
    useBreakpointValue,
    useDisclosure,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    Button
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiArrowDown, FiArrowUp } from 'react-icons/fi';
import { useTranslation } from "react-i18next";
import { useRef, useState } from 'react';

const CategoriesTable = ({ categories, sort, sortDir, onSort, loading, onEdit, onDelete }) => {
    const { t } = useTranslation("default");
    const isMobile = useBreakpointValue({ base: true, md: false });
    const { isOpen, onOpen, onClose } = useDisclosure();
    const cancelRef = useRef();
    const [categoryToDelete, setCategoryToDelete] = useState(null);

    const columns = [
        { key: 'id', label: t('table.th.id') },
        { key: 'name', label: t('table.th.name') },
        { key: 'eventsCount', label: t('table.th.events_count') },
        { key: 'actions', label: t('table.th.actions') },
    ];

    const renderSortIcon = (key) => {
        if (sort !== key) return null;
        return sortDir === 'asc' ? <FiArrowUp /> : <FiArrowDown />;
    };

    const handleDeleteClick = (category) => {
        setCategoryToDelete(category);
        onOpen();
    };

    const handleDeleteConfirm = async () => {
        if (categoryToDelete) {
            await onDelete(categoryToDelete.id);
            setCategoryToDelete(null);
            onClose();
        }
    };

    return (
        <>
            <Box w="100%" maxW="7xl" mx="auto">
                {!isMobile && (
                    <Box
                        display="grid"
                        gridTemplateColumns="150px 1fr 150px 100px"
                        gap="16px"
                        mb="8px"
                    >
                        {columns.map(col => (
                            <Box
                                key={col.key}
                                bg="brand.500"
                                borderRadius="0px"
                                p="12px"
                                display="flex"
                                alignItems="center"
                                fontSize="14px"
                                fontWeight="400"
                                color="white"
                                cursor={col.key !== 'actions' ? 'pointer' : 'default'}
                                onClick={() => col.key !== 'actions' && onSort(col.key)}
                                boxShadow="sm"
                            >
                                <Flex align="center" gap={1}>
                                    {col.label}
                                    {renderSortIcon(col.key)}
                                </Flex>
                            </Box>
                        ))}
                    </Box>
                )}

                {!loading && categories.length === 0 ? (
                    <VStack align="center" gap={'48px'} p={'48px'}>
                        <Text fontFamily="body" fontWeight={'500'} fontSize={'20px'} color="#1E1517">
                            {t('category.list.empty')}
                        </Text>
                    </VStack>
                ) : (
                    categories.map(category => (
                        <Box
                            key={category.id}
                            display="grid"
                            gridTemplateColumns={{
                                base: '1fr',
                                md: '150px 1fr 150px 100px'
                            }}
                            gap="16px"
                            bg="transparent"
                            p="0"
                            mb="12px"
                            fontFamily="body"
                            fontSize="14px"
                            fontWeight="400"
                        >
                            <Box bg="white" borderRadius="0px" p="12px" display="flex" alignItems="center">
                                <Text>{category.id?.substring(0, 8) || '-'}</Text>
                            </Box>

                            <Box bg="white" borderRadius="0px" p="12px" display="flex" alignItems="center">
                                <Text fontWeight="500">{category.name}</Text>
                            </Box>

                            <Box bg="white" borderRadius="0px" p="12px" display="flex" alignItems="center">
                                <Text>{category.eventsCount || 0}</Text>
                            </Box>

                            <Box bg="white" borderRadius="0px" p="8px" display="flex" justifyContent="center" gap={2} alignItems="center">
                                <Tooltip label={t('action.edit')}>
                                    <IconButton
                                        icon={<FiEdit2 />}
                                        aria-label={t('action.edit')}
                                        size="sm"
                                        onClick={() => onEdit(category)}
                                        bg="white"
                                        color="#5C1628"
                                    />
                                </Tooltip>
                                <Tooltip label={t('action.delete')}>
                                    <IconButton
                                        icon={<FiTrash2 />}
                                        aria-label={t('action.delete')}
                                        size="sm"
                                        onClick={() => handleDeleteClick(category)}
                                        bg="white"
                                        color="#DC0000"
                                    />
                                </Tooltip>
                            </Box>
                        </Box>
                    ))
                )}
            </Box>

            <AlertDialog
                isOpen={isOpen}
                leastDestructiveRef={cancelRef}
                onClose={onClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            {t('category.delete.modal_title')}
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            {t('category.delete.modal_confirmation')} "{categoryToDelete?.name}"?
                        </AlertDialogBody>

                        <AlertDialogFooter gap="8px">
                            <Button ref={cancelRef} onClick={onClose} borderRadius={'12px'}>
                                {t('action.cancel')}
                            </Button>
                            <Button colorScheme="red" onClick={handleDeleteConfirm} borderRadius={'12px'}>
                                {t('action.delete')}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    );
};

export default CategoriesTable;
